import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function FullIntegration3Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Full Integration - Part 3
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        대시보드와 고급 기능을 통해 완전한 창고 관리 시스템을 완성해보겠습니다. 
        차트, 고급 검색, 데이터 내보내기, 성능 최적화까지 실무에서 필요한 모든 기능을 구현합니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        대시보드와 고급 기능
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        대시보드 구현
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        통계 카드, 차트, 실시간 모니터링을 포함한 종합 대시보드입니다. 한눈에 비즈니스 현황을 파악할 수 있습니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import { LineChart, PieChart, ResponsiveContainer } from 'recharts';

function Dashboard() {
  const { data, loading } = useQuery(GET_DASHBOARD_DATA);
  
  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>대시보드</Typography>
      
      {/* 통계 카드들 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                총 상품 수
              </Typography>
              <Typography variant="h4">
                {data?.dashboardStats.totalProducts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                이번 달 매출
              </Typography>
              <Typography variant="h4">
                ₩{data?.dashboardStats.monthlyRevenue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                재고 부족
              </Typography>
              <Typography variant="h4">
                {data?.dashboardStats.lowStockProducts}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.light' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                오늘 주문
              </Typography>
              <Typography variant="h4">
                {data?.dashboardStats.todayOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 차트 섹션 */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                매출 추이 (최근 7일)
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data?.salesData}>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="revenue" stroke="#1976d2" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                카테고리별 판매
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie 
                      data={data?.categoryData} 
                      dataKey="value" 
                      nameKey="name"
                      fill="#1976d2"
                    />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        고급 검색 시스템
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        다중 필터, 날짜 범위, 가격 범위 등을 지원하는 고급 검색 기능입니다. 실시간으로 필터가 적용됩니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useState } from 'react';
import { 
  TextField, Select, MenuItem, Slider, 
  DatePicker, Chip, Box, Paper 
} from '@mui/material';

function AdvancedSearch({ onSearch }) {
  const [filters, setFilters] = useState({
    keyword: '',
    category: '',
    priceRange: [0, 1000000],
    stockStatus: '',
    dateRange: [null, null]
  });

  // 디바운스를 사용한 실시간 검색
  const debouncedSearch = useCallback(
    debounce((filters) => {
      onSearch(filters);
    }, 300),
    [onSearch]
  );

  useEffect(() => {
    debouncedSearch(filters);
  }, [filters, debouncedSearch]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const removeFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: key === 'priceRange' ? [0, 1000000] : '' }));
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>고급 검색</Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="키워드 검색"
            fullWidth
            value={filters.keyword}
            onChange={(e) => handleFilterChange('keyword', e.target.value)}
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>카테고리</InputLabel>
            <Select
              value={filters.category}
              label="카테고리"
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="electronics">전자제품</MenuItem>
              <MenuItem value="clothing">의류</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>재고 상태</InputLabel>
            <Select
              value={filters.stockStatus}
              label="재고 상태"
              onChange={(e) => handleFilterChange('stockStatus', e.target.value)}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="in_stock">재고 있음</MenuItem>
              <MenuItem value="low_stock">재고 부족</MenuItem>
              <MenuItem value="out_of_stock">품절</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography gutterBottom>가격 범위</Typography>
          <Slider
            value={filters.priceRange}
            onChange={(e, value) => handleFilterChange('priceRange', value)}
            valueLabelDisplay="auto"
            min={0}
            max={1000000}
            step={10000}
            valueLabelFormat={(value) => \`₩\${value.toLocaleString()}\`}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="시작 날짜"
              value={filters.dateRange[0]}
              onChange={(date) => 
                handleFilterChange('dateRange', [date, filters.dateRange[1]])
              }
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      
      {/* 활성 필터 표시 */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>활성 필터:</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {filters.keyword && (
            <Chip
              label={\`키워드: \${filters.keyword}\`}
              onDelete={() => removeFilter('keyword')}
              size="small"
            />
          )}
          {filters.category && (
            <Chip
              label={\`카테고리: \${filters.category}\`}
              onDelete={() => removeFilter('category')}
              size="small"
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        데이터 내보내기 기능
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Excel과 CSV 형식으로 데이터를 내보낼 수 있는 기능입니다. 대용량 데이터도 효율적으로 처리합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import * as XLSX from 'xlsx';
import { Button, Menu, MenuItem } from '@mui/material';

function ExportButton({ data, filename = 'products' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      data.map(item => ({
        '상품명': item.name,
        '가격': item.price,
        '카테고리': item.category.name,
        '재고': item.inventory.quantity,
        '등록일': new Date(item.createdAt).toLocaleDateString('ko-KR')
      }))
    );
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '상품목록');
    XLSX.writeFile(workbook, \`\${filename}.xlsx\`);
    setAnchorEl(null);
  };
  
  const exportToCSV = () => {
    const csvContent = data.map(item => [
      item.name,
      item.price,
      item.category.name,
      item.inventory.quantity,
      new Date(item.createdAt).toLocaleDateString('ko-KR')
    ]).join('\\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = \`\${filename}.csv\`;
    link.click();
    setAnchorEl(null);
  };

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        내보내기
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={exportToExcel}>Excel 파일</MenuItem>
        <MenuItem onClick={exportToCSV}>CSV 파일</MenuItem>
      </Menu>
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        성능 최적화 기법
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        대용량 데이터 처리, 메모이제이션, 가상화 등을 통한 성능 최적화 방법입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { memo, useMemo, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';

// 메모이제이션으로 불필요한 리렌더링 방지
const ProductItem = memo(({ product, onEdit, onDelete }) => {
  return (
    <Card sx={{ mb: 1 }}>
      <CardContent>
        <Typography>{product.name}</Typography>
        <Button onClick={() => onEdit(product.id)}>편집</Button>
        <Button onClick={() => onDelete(product.id)}>삭제</Button>
      </CardContent>
    </Card>
  );
});

// 가상화 리스트로 대용량 데이터 처리
function VirtualizedProductList({ products }) {
  const Row = useCallback(({ index, style }) => (
    <div style={style}>
      <ProductItem product={products[index]} />
    </div>
  ), [products]);

  return (
    <List
      height={600}
      itemCount={products.length}
      itemSize={120}
    >
      {Row}
    </List>
  );
}

// 커스텀 훅으로 비즈니스 로직 분리
function useProductSearch() {
  const [filters, setFilters] = useState({});
  const [products, setProducts] = useState([]);
  
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      if (filters.keyword && !product.name.includes(filters.keyword)) {
        return false;
      }
      if (filters.category && product.category.id !== filters.category) {
        return false;
      }
      return true;
    });
  }, [products, filters]);

  return {
    products: filteredProducts,
    filters,
    setFilters
  };
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        프로덕션 배포 가이드
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        실제 운영환경에 배포하기 위한 최적화와 모니터링 설정입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`// Dockerfile
FROM node:18-alpine as builder
WORKDIR /app
COPY package*.json ./
RUN npm ci --only=production

COPY . .
RUN npm run build

FROM nginx:alpine
COPY --from=builder /app/build /usr/share/nginx/html
COPY nginx.conf /etc/nginx/nginx.conf
EXPOSE 80
CMD ["nginx", "-g", "daemon off;"]

// 성능 모니터링
import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

function sendToAnalytics(metric) {
  console.log('Web Vital:', metric);
  // Google Analytics나 다른 분석 도구로 전송
}

getCLS(sendToAnalytics);
getFID(sendToAnalytics);
getFCP(sendToAnalytics);
getLCP(sendToAnalytics);
getTTFB(sendToAnalytics);

// 에러 바운더리
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // 에러 리포팅 서비스로 전송
  }

  render() {
    if (this.state.hasError) {
      return <h1>문제가 발생했습니다. 잠시 후 다시 시도해주세요.</h1>;
    }
    return this.props.children;
  }
}`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/docs/full-integration-2')}
        >
          ← PART 2: 실제 컴포넌트 구현
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/')}
        >
          🏠 홈으로 돌아가기
        </Button>
      </Box>
    </Container>
  );
}
